import { useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { api, APIError } from "~/lib/api";
import { cn } from "~/lib/utils";

function uploadErrorMessage(e: unknown): string {
  if (!(e instanceof APIError)) return "No connection. Check your internet and try again.";
  if (e.code === "file_too_large") return "That photo is over 10MB. Choose a smaller one or take a screenshot.";
  if (e.code === "unsupported_type") return "Use a JPEG, PNG, WEBP or HEIC image.";
  return "We could not upload the photo. Try again.";
}

export function AddressProofUpload({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (token: string) => void;
  error?: string | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const shownError = uploadError ?? error;

  async function pick(file?: File | null) {
    if (!file) return;
    setBusy(true);
    setUploadError(null);
    try {
      const uploaded = await api.uploadAddressProof(file);
      onChange(uploaded.addressProof);
      setPreview(URL.createObjectURL(file));
    } catch (e) {
      setUploadError(uploadErrorMessage(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function remove() {
    onChange("");
    setPreview("");
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        className="hidden"
        onChange={(e) => void pick(e.target.files?.[0])}
      />

      {value ? (
        <div className="border-hairline bg-surface flex items-center gap-3 rounded-lg border p-3">
          {preview ? (
            <img src={preview} alt="The address you uploaded" className="size-16 shrink-0 rounded-md object-cover" onError={() => setPreview("")} />
          ) : (
            <div className="bg-muted size-16 shrink-0 rounded-md" aria-hidden />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-medium">Photo attached</p>
            <p className="text-muted-foreground text-[13px]">Sent to the warehouse with your tracking number.</p>
          </div>
          <Button type="button" variant="outline" className="h-11 shrink-0 px-4 text-[14px]" onClick={remove}>
            Remove
          </Button>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className={cn(
            "bg-surface hover:bg-accent/50 flex min-h-24 w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-4 py-4 text-center transition-colors",
            shownError ? "border-destructive" : "border-foreground/25",
          )}
        >
          {busy ? <Loader2 className="size-6 animate-spin" /> : <ImagePlus className="size-6" strokeWidth={1.75} />}
          <span className="text-[15px] font-medium">{busy ? "Uploading…" : "Add a photo or screenshot"}</span>
          {!busy && <span className="text-muted-foreground text-[13px]">From your camera or your gallery, up to 10MB</span>}
        </button>
      )}

      {shownError && !value && (
        <p role="alert" className="text-destructive type-footnote">
          {shownError}
        </p>
      )}
    </div>
  );
}
